import { useState, useEffect } from "react";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FoodReceiptPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/food-orders/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          setOrder(res.data.order);
          return;
        }
        if (res.data.status === "expired") { setStatus("error"); return; }
        attempts++;
        if (attempts < 8) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch { setStatus("error"); }
    };
    poll();
  }, [sessionId]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(30,30,30,1) 0%, rgba(10,10,10,1) 60%)" }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400 font-['Oswald'] text-lg">Bekræfter betaling...</p>
        </div>
      </div>
    );
  }

  if (status !== "success") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(30,30,30,1) 0%, rgba(10,10,10,1) 60%)" }}>
        <div className="text-center max-w-sm">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-['Oswald'] font-bold text-white mb-2">Betaling mislykkedes</h1>
          <p className="text-gray-400 text-sm mb-6">Der opstod en fejl med betalingen. Prøv igen.</p>
          <Link to="/bordbestilling" className="px-6 py-3 rounded-xl font-['Oswald'] font-bold text-sm uppercase bg-amber-500 text-black hover:bg-amber-400 transition-all inline-block">
            Prøv igen
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(30,30,30,1) 0%, rgba(10,10,10,1) 60%)" }}>
      <div className="max-w-md mx-auto">
        {/* Success icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-['Oswald'] font-black text-white tracking-wider" data-testid="receipt-title">
            Betaling gennemført
          </h1>
        </div>

        {/* Receipt card */}
        {order && (
          <div className="bg-black/40 backdrop-blur-sm border border-emerald-500/20 rounded-2xl p-6 mb-6" data-testid="receipt-card">
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-white/10 pb-3">
                <span className="text-gray-400 text-sm">Navn</span>
                <span className="text-white font-medium">{order.name}</span>
              </div>
              <div className="flex justify-between items-center border-b border-white/10 pb-3">
                <span className="text-gray-400 text-sm">Dato</span>
                <span className="text-white font-medium">{order.event_date}</span>
              </div>
              {order.quantity > 0 && (
                <div className="flex justify-between items-center border-b border-white/10 pb-3">
                  <span className="text-gray-400 text-sm">Voksne ({order.quantity} pers.)</span>
                  <span className="text-white font-medium" data-testid="receipt-quantity">{order.dinner_total || order.amount} kr</span>
                </div>
              )}
              {order.child_quantity > 0 && (
                <div className="flex justify-between items-center border-b border-white/10 pb-3">
                  <span className="text-gray-400 text-sm">Børn ({order.child_quantity} pers.)</span>
                  <span className="text-white font-medium" data-testid="receipt-child-qty">{order.child_total} kr</span>
                </div>
              )}
              {order.alacarte_items && order.alacarte_items.length > 0 && (
                <div className="border-b border-white/10 pb-3 space-y-2">
                  <span className="text-gray-400 text-sm block">À la carte</span>
                  {order.alacarte_items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm pl-2">
                      <span className="text-gray-300">{item.name} x{item.qty}</span>
                      <span className="text-white">{item.price * item.qty} kr</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between items-center border-b border-white/10 pb-3">
                <span className="text-gray-400 text-sm">Total betalt</span>
                <span className="text-amber-400 font-bold text-lg">{order.amount} DKK</span>
              </div>
              <div className="pt-2">
                <p className="text-gray-400 text-xs text-center mb-2">Din betalingskode</p>
                <div className="bg-emerald-500/10 border-2 border-emerald-500/30 rounded-xl py-4 text-center">
                  <span className="text-emerald-400 font-black font-mono text-3xl tracking-[0.3em]" data-testid="receipt-code">{order.payment_code}</span>
                </div>
                <p className="text-gray-500 text-xs text-center mt-2">Vis denne kode til personalet</p>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 text-center mb-6">
          <p className="text-amber-400/80 text-sm leading-relaxed">
            Vis denne kvittering i baren når du ankommer.
          </p>
        </div>

        <div className="text-center">
          <Link to="/bordbestilling" className="text-gray-500 text-sm hover:text-white transition-all">
            Tilbage til Bordbestilling
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FoodReceiptPage;
